import { useQuery, type QueryKey } from '@tanstack/react-query';
import service from '../service';
import type {
  EvalSuiteSummary,
  EvalSuiteDetail,
  EvalRunSummary,
  EvalRunDetail,
} from '@Types/eval';

export const evalKeys = {
  suites: ['eval-suites'] as const,
  suitesFiltered: (filter: { status?: 'DRAFT' | 'PUBLISHED' }) =>
    ['eval-suites', filter] as const,
  suite: (id: string) => ['eval-suite', id] as const,
  runs: (filter: { suiteId?: string; soulId?: string; status?: string }) =>
    ['eval-runs', filter] as const,
  run: (id: string) => ['eval-run', id] as const,
};

export function useEvalSuites({
  status,
  enabled = true,
}: { status?: 'DRAFT' | 'PUBLISHED'; enabled?: boolean } = {}) {
  return useQuery<EvalSuiteSummary[]>({
    queryKey: evalKeys.suitesFiltered({ status }) as unknown as QueryKey,
    queryFn: () => service.getEvalSuites({ status }),
    enabled,
  });
}

export function useEvalSuite({
  suiteId,
  enabled = true,
}: {
  suiteId?: string;
  enabled?: boolean;
}) {
  return useQuery<EvalSuiteDetail>({
    queryKey: evalKeys.suite(suiteId!) as unknown as QueryKey,
    queryFn: () => service.getEvalSuite(suiteId!),
    enabled: enabled && !!suiteId,
  });
}

export function useEvalRuns({
  suiteId,
  soulId,
  status,
  enabled = true,
}: {
  suiteId?: string;
  soulId?: string;
  status?: string;
  enabled?: boolean;
} = {}) {
  return useQuery<EvalRunSummary[]>({
    queryKey: evalKeys.runs({ suiteId, soulId, status }) as unknown as QueryKey,
    queryFn: () => service.getEvalRuns({ suiteId, soulId, status }),
    enabled,
  });
}

export function useEvalRun({
  runId,
  enabled = true,
  refetchInterval = false,
}: {
  runId?: string;
  enabled?: boolean;
  /** Pass a number (ms) to poll while the run is RUNNING. */
  refetchInterval?: number | false;
}) {
  return useQuery<EvalRunDetail>({
    queryKey: evalKeys.run(runId!) as unknown as QueryKey,
    queryFn: () => service.getEvalRun(runId!),
    enabled: enabled && !!runId,
    refetchInterval,
  });
}
